import { useCallback, useEffect, useMemo, useState } from "react";
import * as RDialog from "@radix-ui/react-dialog";
import { MessageSquarePlus, Trash2, X } from "lucide-react";

import { APIError, deleteAgentRun, listAgentRuns, type AgentRunSummary } from "@/lib/api";
import { humanizeError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Page";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { cn } from "@/lib/utils";

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ChatHistory is a right-side drawer listing a project's dashboard conversations.
// Click one to continue it, the trash to delete it, or "New chat" to start fresh.
// It reads the current conversation from the same localStorage key the chat panel
// persists, so the open one is highlighted.
export function ChatHistory({
  projectId,
  open,
  onClose,
  onSelect,
  onNewChat,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onSelect: (conversationId: string, runId: string) => void;
  onNewChat: () => void;
}) {
  const [runs, setRuns] = useState<AgentRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();

  const currentRunId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(`cb-agent-conv:${projectId}`) || "{}").runId || "";
    } catch {
      return "";
    }
    // re-read whenever the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, open]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRuns(await listAgentRuns(projectId));
      setErr(null);
    } catch (e) {
      setErr(humanizeError(e, "Couldn't load history"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const onDelete = useCallback(
    async (run: AgentRunSummary) => {
      const ok = await confirm({
        title: "Delete this conversation?",
        description: "Permanently removes this chat history. This can't be undone.",
        confirmText: "Delete",
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteAgentRun(projectId, run.id);
        setRuns((cur) => cur.filter((r) => r.id !== run.id));
        toast.success("Conversation deleted.");
        if (run.id === currentRunId) onNewChat(); // dropped the open one → start fresh
      } catch (e) {
        toast.error(
          e instanceof APIError && e.status === 403
            ? "You need editor access to delete chats."
            : humanizeError(e, "Couldn't delete"),
        );
      }
    },
    [projectId, confirm, toast, currentRunId, onNewChat],
  );

  return (
    <RDialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" />
        <RDialog.Content className="fixed inset-y-0 right-0 z-[60] flex w-full max-w-sm flex-col border-l bg-background shadow-xl outline-none">
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <RDialog.Title className="text-sm font-semibold">Chat history</RDialog.Title>
            <RDialog.Description className="sr-only">Browse, continue or delete past conversations.</RDialog.Description>
            <RDialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Close history">
                <X className="h-4 w-4" />
              </Button>
            </RDialog.Close>
          </div>

          <div className="border-b p-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => {
                onNewChat();
                onClose();
              }}
            >
              <MessageSquarePlus className="h-4 w-4" /> New chat
            </Button>
          </div>

          <div className="scrollbar-thin min-h-0 flex-1 overflow-auto p-2">
            {loading ? (
              <div className="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
                <Spinner /> Loading…
              </div>
            ) : err ? (
              <div className="px-2 py-6 text-sm">
                <span className="text-destructive">{err}</span>{" "}
                <button className="underline" onClick={() => void refresh()}>
                  Retry
                </button>
              </div>
            ) : runs.length === 0 ? (
              <div className="px-2 py-10 text-center text-sm text-muted-foreground">No conversations yet.</div>
            ) : (
              <ul className="space-y-0.5">
                {runs.map((run) => {
                  const active = run.id === currentRunId;
                  return (
                    <li
                      key={run.id}
                      className={cn(
                        "group flex items-start gap-2 rounded-lg px-2 py-2",
                        active ? "bg-primary/10" : "hover:bg-accent",
                      )}
                    >
                      <button
                        className="min-w-0 flex-1 text-left"
                        onClick={() => {
                          onSelect(run.conversation_id, run.id);
                          onClose();
                        }}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className={cn("truncate text-sm font-medium", active && "text-primary")}>
                            {run.title}
                          </span>
                          {run.status === "awaiting_human" && (
                            <span className="shrink-0 text-[11px] text-warning">· waiting</span>
                          )}
                        </div>
                        {run.preview && <div className="truncate text-xs text-muted-foreground">{run.preview}</div>}
                        <div className="mt-0.5 text-[11px] text-muted-foreground/70">{relTime(run.updated_at)}</div>
                      </button>
                      <button
                        aria-label="Delete conversation"
                        onClick={() => void onDelete(run)}
                        className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
